import React, { FC, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import styled from 'styled-components';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { SurveyResponse } from '../../../common/model/dto/survey/survey-response';
import OvButton from '../atoms/OvButton';
import Variables from '../../../design-system/variables';
import Colours from '../../../design-system/colours';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { Question } from '../../../common/model/dto/survey/question';
import { QuestionTypeEnum } from '../../../common/model/dto/survey/question-type.enum';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import PollIcon from '@mui/icons-material/Poll';
import { SurveyQuestionResponse } from '../../../common/model/dto/survey/survey-question-response';
import OvNpsSurveyQuestion from '../atoms/OvNpsSurveyQuestion';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

export interface OvSurveyResponsesProps {
  isOpen: boolean;
  onCancel: () => void;
  surveyResponse: SurveyResponse;
}

const OvViewSurveyResponses: FC<OvSurveyResponsesProps> = ({
  isOpen,
  onCancel,
  surveyResponse,
}) => {
  const { t } = useTranslation();
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(0);

  const goTo = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setActiveQuestionIndex((prevState) => prevState - 1);
    } else if (direction === 'next') {
      setActiveQuestionIndex((prevState) => prevState + 1);
    }
  };

  const getActiveQuestion = (): Question =>
    surveyResponse.questions[activeQuestionIndex];

  const getActiveQuestionResponse = (): SurveyQuestionResponse | undefined =>
    surveyResponse?.question_responses?.find(
      ({ question_id }) => question_id === getActiveQuestion().id
    );

  const getQuestionTypeIcon = (): ReactElement => {
    if (getActiveQuestion()?.type === QuestionTypeEnum.NPS) {
      return <PollIcon />;
    } else if (getActiveQuestion()?.type === QuestionTypeEnum.TEXT) {
      return <TextFieldsIcon />;
    } else if (getActiveQuestion()?.type === QuestionTypeEnum.SINGLE) {
      return <RadioButtonCheckedIcon />;
    } else if (getActiveQuestion()?.type === QuestionTypeEnum.MULTI) {
      return <CheckBoxIcon />;
    }

    return <></>;
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      aria-labelledby={t('surveyResponsesDialog.title')}
      aria-describedby={t('surveyResponsesDialog.description')}
    >
      <StyledDialogContent>
        <QuestionLabel>
          {getQuestionTypeIcon()}
          {getActiveQuestion()?.label}
          {getActiveQuestion()?.required && ' *'}
        </QuestionLabel>

        {(getActiveQuestion()?.type === QuestionTypeEnum.TEXT ||
          getActiveQuestion()?.type === QuestionTypeEnum.SINGLE) && (
          <TextResponse>
            <strong>{t('common.answer')}: </strong>{' '}
            {getActiveQuestionResponse()?.value || '-'}
          </TextResponse>
        )}

        {getActiveQuestion()?.type === QuestionTypeEnum.NPS && (
          <OvNpsSurveyQuestion
            leftLabel={getActiveQuestion()?.question_options?.[0].label}
            rightLabel={getActiveQuestion()?.question_options?.[1].label}
            value={parseInt(getActiveQuestionResponse()?.value, 10)}
          />
        )}

        {getActiveQuestion()?.type === QuestionTypeEnum.MULTI && (
          <TextResponse>
            <strong>{t('common.answer')}: </strong>{' '}
            {getActiveQuestionResponse()?.value?.length
              ? getActiveQuestionResponse()?.value
              : '-'}
          </TextResponse>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        {activeQuestionIndex > 0 && (
          <StyledOvButton
            style={{ paddingLeft: '0.5rem' }}
            onClick={() => goTo('prev')}
          >
            <NavigateBeforeIcon />
            {t('common.actions.prev')}
          </StyledOvButton>
        )}

        {activeQuestionIndex < surveyResponse.questions?.length - 1 && (
          <StyledOvButton
            style={{ paddingRight: '0.5rem' }}
            onClick={() => goTo('next')}
          >
            {t('common.actions.next')}
            <NavigateNextIcon />
          </StyledOvButton>
        )}

        <StyledOvButton onClick={onCancel}>
          {t('common.actions.hide')}
        </StyledOvButton>
      </StyledDialogActions>
    </Dialog>
  );
};

export default OvViewSurveyResponses;

const StyledDialogActions = styled(DialogActions)`
  && {
    padding: 0 1.5rem 1.5rem 1.5rem;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  && {
    width: 48rem;
    max-width: 100%;
    min-height: 8rem;
  }
`;

const QuestionLabel = styled.h3`
  color: ${Colours.OV_BASE_LIGHT};
  font-size: ${Variables.fontSizes.MLARGE};
  margin-bottom: 1.25rem;
  margin-top: 0;
  text-align: center;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
`;

const TextResponse = styled.p`
  margin: 0;
  color: ${Colours['TEXT_DARK']['300']};
  font-style: italic;
`;

const StyledOvButton = styled(OvButton)`
  && {
    padding: 0 1rem;
    border-radius: ${Variables.borderRadius.CLINIC_DASHBOARD_LARGE};
    margin-right: 0.75rem;
    transition: none;
    font-weight: bold;
    text-transform: none;
    margin-left: 1rem !important;
    background-color: ${Colours.OV_BASE};

    &:last-child {
      margin-right: 0;
    }
  }
`;
